/**
 * Cookie preferences page - companion to CookieBanner.
 *
 * Per CHAP-135. Lets a user revisit their consent choices at any time,
 * which is a GDPR requirement (consent must be as easy to withdraw as
 * to give).
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { readConsent, writeConsent } from '../../components/CookieBanner.js';

export default function Cookies() {
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [decidedAt, setDecidedAt] = useState<string | null>(null);
  const [savedToast, setSavedToast] = useState(false);

  useEffect(() => {
    const c = readConsent();
    if (c) {
      setAnalytics(c.analytics);
      setMarketing(c.marketing);
      setDecidedAt(c.decided_at);
    }
  }, []);

  const save = () => {
    writeConsent({ necessary: true, analytics, marketing });
    setDecidedAt(new Date().toISOString());
    setSavedToast(true);
    setTimeout(() => setSavedToast(false), 2500);
  };

  return (
    <article
      style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: '40px 24px',
        fontFamily: 'system-ui, sans-serif',
        color: '#03244d',
        lineHeight: 1.6,
      }}
    >
      <h1 style={{ fontSize: 32, marginBottom: 8 }}>Cookie preferences</h1>
      <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 32 }}>
        You can change these at any time. See our{' '}
        <Link to="/legal/privacy" style={{ color: '#03244d' }}>
          privacy policy
        </Link>{' '}
        for the full picture.
      </p>

      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, marginBottom: 8 }}>Strictly necessary</h2>
        <p style={{ fontSize: 14, color: '#374151', marginBottom: 8 }}>
          Keeps you signed in, remembers your CSRF token, scopes you to the right
          tenant. Required for the application to work; cannot be disabled here.
        </p>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: 0.6 }}>
          <input type="checkbox" checked disabled />
          <span>Always on</span>
        </label>
      </section>

      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, marginBottom: 8 }}>Analytics</h2>
        <p style={{ fontSize: 14, color: '#374151', marginBottom: 8 }}>
          Aggregate usage patterns help us understand which features are useful and
          what's confusing. We use PostHog with auto-capture disabled - only 15
          named events are collected (login, page view, etc.). No PII is sent.
          Off by default.
        </p>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={analytics}
            onChange={(e) => setAnalytics(e.target.checked)}
          />
          <span>Enable product analytics</span>
        </label>
      </section>

      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, marginBottom: 8 }}>Marketing</h2>
        <p style={{ fontSize: 14, color: '#374151', marginBottom: 8 }}>
          We don't use marketing cookies. This option is here for transparency.
        </p>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={marketing}
            onChange={(e) => setMarketing(e.target.checked)}
          />
          <span>Marketing (currently unused)</span>
        </label>
      </section>

      <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
        <button
          type="button"
          onClick={save}
          style={{
            padding: '10px 20px',
            background: '#03244d',
            color: 'white',
            border: 'none',
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Save preferences
        </button>
        {savedToast && (
          <span style={{ alignSelf: 'center', fontSize: 14, color: '#10b981' }}>
            ✓ Saved
          </span>
        )}
      </div>

      {decidedAt && (
        <p style={{ marginTop: 32, fontSize: 12, color: '#6b7280' }}>
          Preferences last updated: {new Date(decidedAt).toLocaleString()}
        </p>
      )}
    </article>
  );
}
